import { localizeErrorText } from '../data/panel';
import { clearSession, readSession, writeSession } from './session-store';

const prefix = '/api/panel/v1';

function localizeFields(fields) {
  if (!fields || typeof fields !== 'object') return {};
  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, localizeErrorText(value) || value]),
  );
}

export class ApiError extends Error {
  constructor(status, payload) {
    const error = payload && payload.error;
    super(localizeErrorText((error && error.message) || '') || 'Сервис временно недоступен, повторите позже');
    this.name = 'ApiError';
    this.status = status;
    this.code = error && error.code;
    this.fields = localizeFields(error && error.fields);
  }
}

let refreshing = null;

async function readPayload(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function buildUrl(path, query) {
  const base = path.startsWith('/api') ? path : `${prefix}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const text = params.toString();
  return text ? `${base}?${text}` : base;
}

async function refreshSession() {
  const current = readSession();
  if (!current) {
    throw new ApiError(401, { error: { code: 'unauthorized', message: 'Сессия истекла' } });
  }
  const response = await fetch(`${prefix}/auth/refresh`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: current.refreshToken }),
  });
  const payload = await readPayload(response);
  if (!response.ok) {
    clearSession();
    window.dispatchEvent(new Event('rhm-session-expired'));
    throw new ApiError(response.status, payload);
  }
  writeSession({
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresIn: payload.expires_in,
    user: payload.user,
  });
  return payload;
}

export async function request(path, options = {}) {
  const { method = 'GET', body, auth = true, retry = true, query } = options;
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const session = readSession();
    if (session) headers.Authorization = `Bearer ${session.accessToken}`;
  }

  let response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, {
      error: { code: 'provider_unavailable', message: 'Сервис временно недоступен, повторите позже' },
    });
  }

  if (response.status === 401 && auth && retry) {
    if (!refreshing) {
      refreshing = refreshSession().finally(() => {
        refreshing = null;
      });
    }
    await refreshing;
    return request(path, { ...options, retry: false });
  }

  const payload = await readPayload(response);
  if (!response.ok) {
    if (response.status === 401 && auth) {
      clearSession();
      window.dispatchEvent(new Event('rhm-session-expired'));
    }
    throw new ApiError(response.status, payload);
  }
  return payload;
}
